/**
 * Kickard Tutorial Configuration — Phase 3.8 onboarding rewrite.
 *
 * DRAW-FIRST model. Every turn starts with DRAW. The drawn card is the
 * action card. Green highlights show valid options.
 *
 * Single source of truth for ALL tutorial copy. The Joker / Double
 * Chance mental model is defined once and reused everywhere (primer,
 * coach marks, glossary, in-match modals) so we never explain it two
 * different ways.
 *
 * CRITICAL RULE — JOKER vs DOUBLE CHANCE:
 *   SHOOT         → direct shot at goal with 1 card. Bypasses opp lines.
 *   DOUBLE CHANCE → NOT a direct shot at goal. Either place on your own
 *                   ATTACK/DEFENSE/GOALKEEPER, or attack an opponent
 *                   field card with 2 cards. Best card counts.
 *   GK with DC    → defends with 2 cards when shot at.
 *
 * NEVER write: "DC shoots at goal", "DC is a 2-card shot", "shoot with DC".
 */

// ---- Primer (6-card carousel, first Bot Match) -----------------------

export interface PrimerCard {
  id: 'board' | 'turn' | 'path' | 'joker' | 'double-chance' | 'cards';
  title: string;
  testid: string;
  lines: string[];
}

export const TUTORIAL_PRIMER: PrimerCard[] = [
  {
    id: 'board',
    title: 'BOARD SETUP',
    testid: 'tutorial-card-board',
    lines: [
      'Cards are dealt automatically.',
      'You start with 3 ATTACK, 2 DEFENSE',
      'and 1 hidden GOALKEEPER.',
    ],
  },
  {
    id: 'turn',
    title: 'YOUR TURN',
    testid: 'tutorial-card-turn',
    lines: [
      'Every action starts with DRAW.',
      'The card you draw is your action card.',
      'Use it to attack or fill an empty slot.',
    ],
  },
  {
    id: 'path',
    title: 'PATH TO GOAL',
    testid: 'tutorial-card-path',
    lines: [
      'Remove 2 opponent ATTACK cards.',
      'Then DEFENSE opens.',
      'Remove 1 DEFENSE card.',
      'Then GOALKEEPER opens.',
      'Beat GOALKEEPER to score.',
    ],
  },
  {
    id: 'joker',
    title: 'JOKER CHOICE',
    testid: 'tutorial-card-joker',
    lines: [
      'Joker gives you two options:',
      'SHOOT directly at goal with 1 card,',
      'or use DOUBLE CHANCE.',
    ],
  },
  {
    id: 'double-chance',
    title: 'DOUBLE CHANCE',
    testid: 'tutorial-card-double-chance',
    lines: [
      'Double Chance does not shoot at goal.',
      'Place it on your field',
      'or attack an opponent field card with 2 cards.',
      'Best card counts.',
    ],
  },
  {
    id: 'cards',
    title: 'CARD RULES',
    testid: 'tutorial-card-cards',
    lines: [
      'Higher card wins.',
      'Tie = nothing.',
      'A beats everything except 2.',
      '2 beats A.',
    ],
  },
];

// ---- Coach marks (in-match, contextual) ------------------------------

export interface CoachMarkCopy {
  id: string;
  title: string;
  body: string[];
  /** Optional anchor: which UI element this mark conceptually points at. */
  anchor?: 'draw' | 'drawn-card' | 'board' | 'opponent-attack' | 'opponent-defense' | 'goalkeeper';
}

export const COACH_MARKS: Record<string, CoachMarkCopy> = {
  firstTurn: {
    id: 'firstTurn',
    title: 'YOUR TURN',
    body: [
      'Tap DRAW.',
      'The card you draw is your action card.',
    ],
    anchor: 'draw',
  },
  afterDraw: {
    id: 'afterDraw',
    title: 'USE YOUR DRAWN CARD',
    body: [
      'Green highlights show your valid options.',
      'Attack a highlighted opponent card or fill one of your empty slots.',
    ],
    anchor: 'drawn-card',
  },
  branchAttack: {
    id: 'branchAttack',
    title: 'ATTACK',
    body: [
      'Your drawn card attacks this opponent card.',
      'Higher card wins. Tie = nothing.',
    ],
    anchor: 'opponent-attack',
  },
  branchPlace: {
    id: 'branchPlace',
    title: 'BUILD YOUR FORMATION',
    body: [
      'You placed the drawn card into your formation.',
      'Empty slots give you more options.',
    ],
  },
  breakAttackFirst: {
    id: 'breakAttackFirst',
    title: 'BREAK ATTACK FIRST',
    body: [
      'Opponent has 3 ATTACK cards.',
      'Remove 2 first.',
      'When only 1 ATTACK remains, DEFENSE opens.',
    ],
    anchor: 'opponent-attack',
  },
  defenseOpen: {
    id: 'defenseOpen',
    title: 'DEFENSE OPEN',
    body: [
      'Only 1 opponent ATTACK card remains.',
      'Now you can attack DEFENSE.',
    ],
    anchor: 'opponent-defense',
  },
  breakDefense: {
    id: 'breakDefense',
    title: 'BREAK DEFENSE',
    body: [
      'Opponent has 2 DEFENSE cards.',
      'Remove 1 first.',
      'When only 1 DEFENSE remains, GOALKEEPER opens.',
    ],
    anchor: 'opponent-defense',
  },
  goalkeeperOpen: {
    id: 'goalkeeperOpen',
    title: 'GOALKEEPER OPEN',
    body: [
      'Only 1 opponent DEFENSE card remains.',
      'Now you can attack the GOALKEEPER.',
    ],
    anchor: 'goalkeeper',
  },
  firstGoalkeeper: {
    id: 'firstGoalkeeper',
    title: 'GOALKEEPER',
    body: [
      'The goalkeeper is the last line.',
      'Beat the goalkeeper to score.',
    ],
    anchor: 'goalkeeper',
  },
  firstBlock: {
    id: 'firstBlock',
    title: 'BLOCKED',
    body: [
      'This line is still protected.',
      'Keep removing cards to open the next line.',
    ],
  },
  firstPenalties: {
    id: 'firstPenalties',
    title: 'PENALTIES',
    body: [
      'The deck is finished and the score is level.',
      'Penalties decide the match.',
    ],
  },
  timerWarning: {
    id: 'timerWarning',
    title: 'HURRY',
    body: ['Make your move before time runs out.'],
  },
};

// ---- Joker / Double Chance explainer ---------------------------------

export const JOKER_EXPLAINER = {
  id: 'jokerExplainer',
  testid: 'tutorial-joker-explainer',
  title: 'JOKER',
  intro: 'Joker gives you two options:',
  options: [
    {
      label: 'SHOOT',
      detail: 'Direct shot at goal with 1 card. Bypasses ATTACK and DEFENSE lines.',
    },
    {
      label: 'DOUBLE CHANCE',
      detail: 'Not a direct shot at goal. Place it on your field, or attack an opponent field card with 2 cards. Best card counts.',
    },
  ],
  footer: [
    'SHOOT uses exactly 1 card and goes straight at goal.',
    'DOUBLE CHANCE never shoots directly at goal.',
  ],
};

export const DOUBLE_CHANCE_EXPLAINER = {
  id: 'doubleChanceExplainer',
  testid: 'tutorial-double-chance-explainer',
  title: 'DOUBLE CHANCE',
  intro: 'Double Chance does not shoot directly at goal.',
  body: [
    'Attack an opponent ATTACK or DEFENSE card with 2 cards.',
    'Or place it on your own ATTACK, DEFENSE or GOALKEEPER.',
    'Best card counts.',
  ],
  clarification: [
    'If placed on your GOALKEEPER, it defends with 2 cards when shot at.',
    'Double Chance never bypasses the opponent\u2019s lines.',
  ],
};

// ---- Glossary (HELP bottom sheet) ------------------------------------
//
// Multi-line definitions — rendered as stacked paragraphs so the
// path-to-goal entries are readable on mobile.

export interface GlossaryEntry {
  term: string;
  lines: string[];
}

export const GLOSSARY: GlossaryEntry[] = [
  {
    term: 'TURN',
    lines: [
      'Your turn can include multiple draws.',
      'Keep drawing while your drawn card has a valid move.',
      'If your drawn card cannot attack or fill an empty slot, the opponent gets the turn.',
    ],
  },
  {
    term: 'DRAW',
    lines: [
      'Every action starts by drawing a card.',
      'The drawn card is your action card.',
    ],
  },
  {
    term: 'BOARD',
    lines: [
      'You start with 3 ATTACK, 2 DEFENSE and 1 hidden GOALKEEPER.',
      'Cards are dealt automatically.',
    ],
  },
  {
    term: 'GREEN HIGHLIGHT',
    lines: ['Green shows valid moves after you draw.'],
  },
  {
    term: 'ATTACK',
    lines: ['Use your drawn card to attack a highlighted opponent card.'],
  },
  {
    term: 'PATH TO GOAL',
    lines: [
      'Opponent starts with 3 ATTACK cards.',
      'Remove 2 ATTACK cards. DEFENSE opens.',
      'Remove 1 DEFENSE card. GOALKEEPER opens.',
      'Beat GOALKEEPER to score.',
    ],
  },
  {
    term: 'GOALKEEPER',
    lines: [
      'Hidden until the shot reaches goal.',
      'Beat the goalkeeper to score.',
    ],
  },
  {
    term: 'A vs 2',
    lines: [
      'A beats everything except 2.',
      '2 beats A.',
    ],
  },
  {
    term: 'JOKER',
    lines: [
      'Joker gives you two options: SHOOT or DOUBLE CHANCE.',
      'SHOOT goes directly at goal with 1 card, even if ATTACK and DEFENSE are still blocking the path.',
    ],
  },
  {
    term: 'DOUBLE CHANCE',
    lines: [
      'Double Chance does not shoot directly at goal.',
      'Use it to attack an opponent field card with 2 cards,',
      'or place it on your ATTACK, DEFENSE or GOALKEEPER.',
      'If placed on GOALKEEPER, it defends with 2 cards later.',
      'Best card counts.',
    ],
  },
  {
    term: 'PENALTIES',
    lines: ['Used when the match is level after the deck ends.'],
  },
];
